/**
 * AllocationGrantModal — guided modal for granting allocations or applying tier presets.
 *
 * WQ-230 (Phase 4.9 α4). Three modes via `mode` prop:
 *   - "grant": single allocation grant (capability picker + tier preset + optional override).
 *   - "tier-preset": bulk-apply tier preset to a tenant.
 *   - "edit": edit an existing allocation's quota (uses grant() endpoint per its idempotent shape).
 *
 * If `scopedTenantId` is provided, the tenant picker is locked to that tenant.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  grantAllocation,
  applyTierPreset,
  type Allocation,
  type GrantRequest,
} from '../../services/allocationAdminService';

const CAPABILITY_TYPES = [
  'prediction',
  'automl',
  'monte_carlo',
  'fairness',
  'explainability',
  'data_quality',
  'dataset_composition',
  'model',
  'dataset',
  'ai_action',
];

const TIER_PRESETS = ['free', 'pro', 'enterprise', 'custom'];
const QUOTA_PERIODS = ['hour', 'day', 'month'];

type Mode = 'grant' | 'tier-preset' | 'edit';

interface TenantOption {
  id: string;
  name: string;
}

interface AllocationGrantModalProps {
  isOpen: boolean;
  mode: Mode;
  scopedTenantId?: string;
  /** When mode is 'edit', the existing allocation being modified */
  editing?: Allocation | null;
  /** Tenant options to populate the picker. If empty + no scopedTenantId, picker shows
   * a free-text tenant_id input. (Real tenant list fetched by parent if available.) */
  tenants?: TenantOption[];
  /** Current operator's email (defaults granted_by/revoked_by inputs) */
  currentUserEmail?: string;
  onClose: (didCommit: boolean) => void;
}

const AllocationGrantModal: React.FC<AllocationGrantModalProps> = ({
  isOpen,
  mode,
  scopedTenantId,
  editing,
  tenants = [],
  currentUserEmail = '',
  onClose,
}) => {
  const { authFetch } = useAuth();
  const [tenantId, setTenantId] = useState('');
  const [capabilityType, setCapabilityType] = useState('prediction');
  const [capabilityId, setCapabilityId] = useState('');
  const [tierPreset, setTierPreset] = useState('free');
  const [quotaUnits, setQuotaUnits] = useState<string>('');
  const [quotaPeriod, setQuotaPeriod] = useState('day');
  const [grantedBy, setGrantedBy] = useState('');
  const [notes, setNotes] = useState('');
  const [onlyMissing, setOnlyMissing] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset / initialize state when modal opens or mode/editing changes
  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setSubmitting(false);
    setTenantId(scopedTenantId || editing?.tenant_id || '');
    setGrantedBy(currentUserEmail);
    setNotes(editing?.notes || '');
    if (editing) {
      setCapabilityType(editing.capability_type);
      setCapabilityId(editing.capability_id || '');
      setTierPreset(editing.tier_preset || 'custom');
      setQuotaUnits(editing.quota_units != null ? String(editing.quota_units) : '');
      setQuotaPeriod(editing.quota_period || 'day');
    } else if (mode !== 'tier-preset') {
      setCapabilityType('prediction');
      setCapabilityId('');
      setTierPreset('free');
      setQuotaUnits('');
      setQuotaPeriod('day');
    }
  }, [isOpen, mode, scopedTenantId, editing, currentUserEmail]);

  if (!isOpen) return null;

  const validate = (): string | null => {
    if (!tenantId) return 'Tenant is required.';
    if (mode === 'grant' || mode === 'edit') {
      if (!capabilityType) return 'Capability type is required.';
      if (tierPreset === 'custom') {
        const n = Number(quotaUnits);
        if (!quotaUnits || Number.isNaN(n) || n < 0) {
          return 'quota_units required (non-negative number) when tier_preset = custom.';
        }
        if (!quotaPeriod) return 'quota_period required when tier_preset = custom.';
      }
    }
    if (mode === 'tier-preset' && !grantedBy) return 'granted_by is required.';
    return null;
  };

  const handleSubmit = async () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (mode === 'tier-preset') {
        await applyTierPreset(authFetch, {
          tenant_id: tenantId,
          tier_preset: tierPreset,
          granted_by: grantedBy,
          only_missing: onlyMissing,
        });
      } else {
        const body: GrantRequest = {
          tenant_id: tenantId,
          capability_type: capabilityType,
          capability_id: capabilityId || null,
          tier_preset: tierPreset,
          granted_by: grantedBy || undefined,
          notes: notes || undefined,
        };
        if (tierPreset === 'custom') {
          body.quota_units = Number(quotaUnits);
          body.quota_period = quotaPeriod;
        }
        await grantAllocation(authFetch, body);
      }
      onClose(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed');
      setSubmitting(false);
    }
  };

  const titleByMode: Record<Mode, string> = {
    grant: 'Grant Allocation',
    'tier-preset': 'Apply Tier Preset',
    edit: 'Edit Allocation Quota',
  };

  const submitLabel: Record<Mode, string> = {
    grant: 'Grant',
    'tier-preset': 'Apply',
    edit: 'Save',
  };

  return (
    <div
      className="modal-overlay"
      onClick={() => onClose(false)}
      data-testid="allocation-grant-modal"
    >
      <div
        className="modal modal-md"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '560px', maxHeight: '85vh', overflow: 'auto' }}
      >
        <div className="modal-header">
          <h2>{titleByMode[mode]}</h2>
          <button className="btn btn-ghost" onClick={() => onClose(false)} aria-label="Close">
            {'\u00d7'}
          </button>
        </div>
        <div className="modal-body" style={{ display: 'grid', gap: '12px' }}>
          {error && (
            <div
              role="alert"
              style={{
                padding: '8px 12px',
                background: 'rgba(220,38,38,0.1)',
                border: '1px solid rgba(220,38,38,0.4)',
                borderRadius: '6px',
                color: 'var(--danger, #dc2626)',
                fontSize: '13px',
              }}
            >
              {error}
            </div>
          )}

          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span>Tenant</span>
            {scopedTenantId || mode === 'edit' ? (
              <input value={tenantId} disabled readOnly aria-label="Tenant ID" />
            ) : tenants.length > 0 ? (
              <select
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                aria-label="Tenant"
              >
                <option value="">— select tenant —</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                placeholder="tenant UUID"
                aria-label="Tenant ID"
              />
            )}
          </label>

          {(mode === 'grant' || mode === 'edit') && (
            <>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span>Capability Type</span>
                <select
                  value={capabilityType}
                  onChange={(e) => setCapabilityType(e.target.value)}
                  disabled={mode === 'edit'}
                  aria-label="Capability Type"
                >
                  {CAPABILITY_TYPES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span>Capability ID (optional — leave blank for wildcard)</span>
                <input
                  value={capabilityId}
                  onChange={(e) => setCapabilityId(e.target.value)}
                  disabled={mode === 'edit'}
                  placeholder="e.g. model UUID, dataset UUID, or blank"
                  aria-label="Capability ID"
                />
              </label>
            </>
          )}

          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span>Tier Preset</span>
            <select
              value={tierPreset}
              onChange={(e) => setTierPreset(e.target.value)}
              aria-label="Tier Preset"
            >
              {TIER_PRESETS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>

          {tierPreset === 'custom' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span>Quota Units</span>
                <input
                  type="number"
                  min="0"
                  value={quotaUnits}
                  onChange={(e) => setQuotaUnits(e.target.value)}
                  aria-label="Quota Units"
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span>Quota Period</span>
                <select
                  value={quotaPeriod}
                  onChange={(e) => setQuotaPeriod(e.target.value)}
                  aria-label="Quota Period"
                >
                  {QUOTA_PERIODS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}

          {mode === 'tier-preset' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={onlyMissing}
                onChange={(e) => setOnlyMissing(e.target.checked)}
                aria-label="Only Missing"
              />
              <span>Only grant capabilities the tenant doesn't already have</span>
            </label>
          )}

          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span>Granted By (operator email)</span>
            <input
              value={grantedBy}
              onChange={(e) => setGrantedBy(e.target.value)}
              placeholder="ops@example.com"
              aria-label="Granted By"
            />
          </label>

          {mode !== 'tier-preset' && (
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span>Notes (optional)</span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                aria-label="Notes"
              />
            </label>
          )}
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={() => onClose(false)} disabled={submitting}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Submitting…' : submitLabel[mode]}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AllocationGrantModal;
